import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const getAdminToken = () => localStorage.getItem('adminKey') || '';

const AdminEventTester = () => {
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('unknown');
  const [tradingBlock, setTradingBlock] = useState(0);
  const [marketOpen, setMarketOpen] = useState(false);
  const [customPartyText, setCustomPartyText] = useState('');
  const [esp32Panels, setEsp32Panels] = useState([]);

  // Fetch current state on load
  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/party-state`);
        if (res.data.partyState) {
          setCurrentPhase(res.data.partyState.phase || 'unknown');
          setTradingBlock(res.data.partyState.tradingBlockNumber || 0);
        }
        if (res.data.marketState) {
          setMarketOpen(res.data.marketState.isOpen);
        }
      } catch (e) {
        console.log('Could not fetch state');
      }
      try {
        const panelRes = await axios.get(`${API_BASE_URL}/api/admin/battleship/panels`, {
          headers: { 'x-admin-token': getAdminToken() }
        });
        if (panelRes.data.panels) {
          setEsp32Panels(panelRes.data.panels.filter(p => p.connected));
        }
      } catch (e) { /* ignore */ }
    };
    fetchState();
    const interval = setInterval(fetchState, 5000);
    return () => clearInterval(interval);
  }, []);

  const sendEvent = async (endpoint, data, successMsg) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, data, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      setMessage({ text: `✓ ${successMsg || response.data.message || 'Success'}`, type: 'success' });
    } catch (error) {
      setMessage({ text: `✗ ${error.response?.data?.error || error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Jump to Phase buttons - for testing any phase immediately
  const jumpToPhaseButtons = [
    {
      label: '🔄 Reset Party',
      color: '#ef4444',
      action: () => sendEvent('/api/admin/party/restart', {}, 'Party reset to beginning'),
      desc: 'Reset everything to not_started'
    },
    {
      label: '📝 → Registration',
      color: '#3b82f6',
      action: () => sendEvent('/api/admin/show/registration', { action: 'start' }, 'Jumped to Registration'),
      desc: 'Start registration phase'
    },
    {
      label: '📺 → Premarket',
      color: '#7c4dff',
      action: () => sendEvent('/api/admin/show/premarket', { action: 'start', minutes: 5 }, 'Jumped to Premarket (5 min)'),
      desc: 'Start 5-min premarket'
    },
    {
      label: '🎬 → Orientation',
      color: '#9c27b0',
      action: () => sendEvent('/api/admin/show/orientation', { action: 'start' }, 'Jumped to Orientation'),
      desc: 'Start orientation'
    },
    {
      label: '🌏 → Phase 1 (Asian)',
      color: '#00bcd4',
      action: () => sendEvent('/api/admin/trading-phase/start', { phase: 1 }, 'Started Phase 1 (Asian Session)'),
      desc: 'Open market + start trading'
    },
    {
      label: '🇪🇺 → Phase 2 (European)',
      color: '#2196f3',
      action: () => sendEvent('/api/admin/trading-phase/start', { phase: 2 }, 'Started Phase 2 (European Session)'),
      desc: 'Start Phase 2 trading'
    },
    {
      label: '🇨🇦 → Phase 3 (Canadian)',
      color: '#f44336',
      action: () => sendEvent('/api/admin/trading-phase/start', { phase: 3 }, 'Started Phase 3 (Canadian Session)'),
      desc: 'Start Phase 3 trading'
    },
    {
      label: '⚡ → Phase 4 (Power Hour)',
      color: '#ff5722',
      action: () => sendEvent('/api/admin/trading-phase/start', { phase: 4 }, 'Started Phase 4 (Power Hour)'),
      desc: 'Start 5-min Power Hour'
    },
    {
      label: '⏸️ → Pause Trading',
      color: '#ff9800',
      action: () => sendEvent('/api/admin/trading/pause', {}, 'Trading paused'),
      desc: 'Halt trading for macro game'
    },
    {
      label: '🏆 → Results',
      color: '#ffd700',
      action: async () => {
        await sendEvent('/api/admin/market/toggle', { action: 'close' }, 'Market closed');
        await sendEvent('/api/admin/show/final-results', { topCount: 10 }, 'Showing results');
      },
      desc: 'Close market + show results'
    }
  ];

  // Party Flow - matches AdminTimeline exactly
  const partyFlowSteps = [
    // Pre-party
    { step: 1, label: '📝 Start Registration', color: '#3b82f6', endpoint: '/api/admin/show/registration', body: { action: 'start' } },
    { step: 2, label: '📺 Start Premarket (30m)', color: '#7c4dff', endpoint: '/api/admin/show/premarket', body: { action: 'start', minutes: 30 } },
    { step: 3, label: '🎬 Play Orientation', color: '#9c27b0', endpoint: '/api/admin/show/orientation', body: { action: 'start' } },
    { step: 4, label: '✓ End Orientation', color: '#673ab7', endpoint: '/api/admin/show/orientation', body: { action: 'end' } },

    // Opening Bell (HARD countdown)
    { step: 5, label: '⏱️ Opening Countdown (HARD 3m)', color: '#10b981', endpoint: '/api/admin/show/countdown', body: { seconds: 180, autoOpenMarket: true, countdownType: 'market-open' }, isHard: true },
    { step: '5b', label: '🔔 OR: Open Market NOW', color: '#22c55e', endpoint: '/api/admin/market/toggle', body: { action: 'open' } },

    // Phase 1 - Asian Session
    { step: 6, label: '🌏 Start Phase 1 (soft)', color: '#00bcd4', endpoint: '/api/admin/trading-phase/start', body: { phase: 1 } },
    { step: 7, label: '🔔 Portfolio Countdown (HARD)', color: '#e040fb', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'Portfolio Warfare', seconds: 180 }, isHard: true },
    { step: '7b', label: '⏸️ OR: Pause NOW', color: '#ff9800', endpoint: '/api/admin/trading/pause', body: {} },

    // Phase 2 - European Session
    { step: 8, label: '🇪🇺 Start Phase 2 (soft)', color: '#2196f3', endpoint: '/api/admin/trading-phase/start', body: { phase: 2 } },
    { step: 9, label: '🔔 Dead Cat Countdown (HARD)', color: '#00c853', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'Dead Cat Bounce', seconds: 180 }, isHard: true },
    { step: '9b', label: '⏸️ OR: Pause NOW', color: '#ff9800', endpoint: '/api/admin/trading/pause', body: {} },

    // Phase 3 - Canadian Session
    { step: 10, label: '🇨🇦 Start Phase 3 (soft)', color: '#f44336', endpoint: '/api/admin/trading-phase/start', body: { phase: 3 } },
    { step: 11, label: '🔔 Briefcase Countdown (HARD)', color: '#ffd700', endpoint: '/api/admin/show/macro-countdown', body: { gameName: 'A Brief Case', seconds: 180 }, isHard: true },
    { step: '11b', label: '⏸️ OR: Pause NOW', color: '#ff9800', endpoint: '/api/admin/trading/pause', body: {} },

    // Phase 4 - Power Hour
    { step: 12, label: '⚡ Start Phase 4 (soft)', color: '#ff5722', endpoint: '/api/admin/trading-phase/start', body: { phase: 4 } },

    // Closing Bell
    { step: 13, label: '🔔 Closing Bell (HARD 1m)', color: '#ef4444', endpoint: '/api/admin/show/close-countdown', body: { seconds: 60, autoCloseMarket: true }, isHard: true },
    { step: '13b', label: '🔔 OR: Close Market NOW', color: '#dc2626', endpoint: '/api/admin/market/toggle', body: { action: 'close' } },

    // Optional Liquidity Crisis
    { step: 14, label: '🌀 Run Liquidity Crisis (optional)', color: '#00e676', endpoint: '/api/admin/big-game/announce', body: { gameName: 'Liquidity Crisis', announcement: 'Liquidity Crisis finale!', pauseTrading: false } },
    { step: '14b', label: '⏭️ OR: Skip to Winners', color: '#ffd700', endpoint: '/api/admin/show/final-results', body: { topCount: 10 } },

    // Results
    { step: 15, label: '📊 Show Final Results', color: '#ffd700', endpoint: '/api/admin/show/final-results', body: { topCount: 10 } },
    { step: 16, label: '🥉 Announce 3rd', color: '#cd7f32', endpoint: '/api/admin/show/winner-announcement', body: { place: 3 } },
    { step: 17, label: '🥈 Announce 2nd', color: '#c0c0c0', endpoint: '/api/admin/show/winner-announcement', body: { place: 2 } },
    { step: 18, label: '🥇 Announce 1st', color: '#ffd700', endpoint: '/api/admin/show/winner-announcement', body: { place: 1 } }
  ];

  // Power Hour toggle (visual-only — does NOT change game state)
  const startPowerHour = async () => {
    await sendEvent('/api/admin/events/broadcast', { type: 'ambient:power-hour-on' }, 'Power Hour visual ON');
  };
  const stopPowerHour = async () => {
    await sendEvent('/api/admin/events/broadcast', { type: 'ambient:power-hour-off' }, 'Power Hour visual OFF');
  };

  // Visual effect test groups
  const effectGroups = [
    {
      title: 'Countdowns (Quick Test)',
      events: [
        { label: '5s Open', color: '#10b981', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 5, autoOpenMarket: false, countdownType: 'market-open' } },
        { label: '10s Open', color: '#22c55e', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 10, autoOpenMarket: false, countdownType: 'market-open' } },
        { label: '10s Game', color: '#a855f7', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 10, autoOpenMarket: false, countdownType: 'macro-game' } },
        { label: '10s Close', color: '#ef4444', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 10, autoOpenMarket: false, countdownType: 'market-close' } },
        { label: '30s Open', color: '#059669', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 30, autoOpenMarket: false, countdownType: 'market-open' } },
        { label: '30s Game', color: '#7c3aed', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 30, autoOpenMarket: false, countdownType: 'macro-game' } },
        { label: '60s Open', color: '#047857', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 60, autoOpenMarket: false, countdownType: 'market-open' } },
        { label: '60s Close', color: '#b91c1c', isCountdown: true, endpoint: '/api/admin/show/countdown', body: { seconds: 60, autoOpenMarket: false, countdownType: 'market-close' } },
      ]
    },
    {
      title: 'Market Animations (with test tickers)',
      events: [
        { label: '💵 Money Rain', color: '#4caf50', type: 'market:money-rain', duration: 5, eventName: 'TRADING RESUMED' },
        { label: '📈 Stock Up', color: '#00c853', type: 'market:stock-up', duration: 3, tickers: ['BGNX'], changePercent: 12.5, eventName: 'FDA Approval' },
        { label: '📉 Stock Down', color: '#ff1744', type: 'market:stock-down', duration: 3, tickers: ['MNFR'], changePercent: -14.2, eventName: 'CEO Scandal' },
        { label: '🐂 Bull Run', color: '#00e676', type: 'market:bull-run', duration: 5, tickers: ['BGNX', 'SOLR'], changePercent: 17.8, eventName: 'Merger Approved' },
        { label: '📉 Market Crash', color: '#d50000', type: 'market:crash', duration: 5, tickers: ['MNFR'], changePercent: -18.3, eventName: 'Data Breach' }
      ]
    },
    {
      title: '🔥 Epic Events (with test tickers)',
      events: [
        { label: '💥 EPIC CRASH', color: '#b71c1c', type: 'market:epic-crash', duration: 8, tickers: ['SOLR', 'MNFR'], changePercent: -25.0, eventName: 'Market Meltdown' },
        { label: '🚀 EPIC BULL', color: '#1b5e20', type: 'market:epic-bull', duration: 8, tickers: ['BGNX'], changePercent: 22.5, eventName: 'FDA Approval' },
        { label: '⛔ Circuit Breaker', color: '#ff6f00', type: 'market:circuit-breaker', duration: 5, eventName: 'CLOSING BELL' },
        { label: '🚀 Rocket', color: '#f57c00', type: 'market:rocket', duration: 4, eventName: 'OPENING BELL' },
        { label: '🪙 Coins Burst', color: '#ffc107', type: 'market:coins-burst', duration: 3, eventName: 'DEAD CAT BOUNCE' }
      ]
    },
    {
      title: '💼 A Brief Case (Full Flow)',
      events: [
        { label: 'Game Start', color: '#ffd700', type: 'sprite:briefcase-run', duration: 10, eventName: 'A BRIEF CASE!' },
        // Question phase
        { label: 'Question Picked', color: '#2196f3', type: 'game:question', duration: 2, eventName: '$800 QUESTION' },
        { label: '✅ Right Answer', color: '#4caf50', type: 'briefcase:right', duration: 2, eventName: 'CORRECT! +$800' },
        { label: '❌ Wrong Answer', color: '#f44336', type: 'briefcase:wrong', duration: 2, eventName: 'WRONG!' },
        // Briefcase phase
        { label: 'Suspense', color: '#e6c200', type: 'briefcase:suspense', duration: 4, eventName: 'SUSPENSE...' },
        { label: 'Reveal: Money', color: '#4caf50', type: 'briefcase:reveal-good', duration: 3, eventName: 'JACKPOT! +$1000' },
        { label: 'Reveal: Bad', color: '#455a64', type: 'briefcase:reveal-bad', duration: 3, eventName: 'ONLY $200...' },
        // Special briefcases
        { label: 'Steal', color: '#f44336', type: 'game:briefcase-steal', duration: 4, eventName: 'STOLEN! Beta +$500' },
        { label: 'Swap', color: '#ff8c00', type: 'game:briefcase-swap', duration: 4, eventName: 'SWAP! Alpha <> Beta' },
        { label: 'Transfer', color: '#a020f0', type: 'game:briefcase-transfer', duration: 4, eventName: 'TRANSFER! Alpha -> Beta' },
        { label: '2X Double', color: '#ffd700', type: 'briefcase:double', duration: 3, eventName: 'DOUBLE! Team Alpha' },
        { label: '½ Half', color: '#ff5722', type: 'briefcase:half', duration: 3, eventName: 'HALVED! Team Alpha' },
        { label: '? Mystery', color: '#9c27b0', type: 'briefcase:mystery', duration: 3, eventName: '??? Team Alpha' },
        // End
        { label: 'Game Winner', color: '#4caf50', type: 'game:winner', duration: 5, eventName: 'WINNER: Team Alpha' },
        { label: 'Game End', color: '#607d8b', type: 'briefcase:end', duration: 3, eventName: 'GAME OVER' }
      ]
    },
    {
      title: '💼 Portfolio Warfare (Full Flow)',
      events: [
        { label: 'Game Start', color: '#4caf50', type: 'sprite:sinking-ship', duration: 12, eventName: 'PORTFOLIO WARFARE' },
        { label: 'Turn: Red', color: '#f44336', type: 'battleship:turn', duration: 2, eventName: "red's TURN" },
        { label: 'Turn: Blue', color: '#2196f3', type: 'battleship:turn', duration: 2, eventName: "blue's TURN" },
        { label: 'Turn: Green', color: '#4caf50', type: 'battleship:turn', duration: 2, eventName: "green's TURN" },
        { label: 'Turn: Yellow', color: '#e6c200', type: 'battleship:turn', duration: 2, eventName: "yellow's TURN" },
        { label: 'Hit!', color: '#ff5722', type: 'battleship:hit', duration: 3, eventName: 'HIT! red -> blue' },
        { label: 'Miss', color: '#0064c8', type: 'battleship:miss', duration: 2, eventName: 'MISS red -> green' },
        { label: 'Sunk!', color: '#d50000', type: 'battleship:sunk', duration: 4, eventName: 'SUNK! blue Destroyer' },
        { label: 'Eliminated!', color: '#b71c1c', type: 'battleship:eliminated', duration: 4, eventName: 'yellow OUT!' },
        { label: 'Victory!', color: '#ffd700', type: 'battleship:game-over', duration: 5, eventName: 'WINNER: red' },
        { label: 'Game End', color: '#607d8b', type: 'battleship:game-end', duration: 3, eventName: 'GAME OVER' }
      ]
    },
    {
      title: '⛳ Dead Cat Bounce (Game Flow)',
      events: [
        { label: 'Game Start', color: '#00c853', type: 'sprite:dead-cat', duration: 10, eventName: 'DEAD CAT BOUNCE!' },
        { label: 'Level 1', color: '#4caf50', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 1!' },
        { label: 'Level 2', color: '#8bc34a', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 2!' },
        { label: 'Level 3', color: '#ff9800', type: 'minigolf:level', duration: 3, eventName: 'LEVEL 3!' },
        { label: 'Score!', color: '#4caf50', type: 'minigolf:score', duration: 2, eventName: 'SCORE!' },
        { label: 'Game End', color: '#607d8b', type: 'minigolf:end', duration: 3, eventName: 'GAME OVER' }
      ]
    },
    {
      title: '⛳ Good Shots',
      events: [
        { label: '✅ Nice Shot!', color: '#4caf50', type: 'minigolf:good', duration: 2, eventName: 'NICE SHOT!' },
        { label: '🎯 Smooth!', color: '#66bb6a', type: 'minigolf:smooth', duration: 2, eventName: 'SMOOTH!' },
        { label: '🧈 Butter!', color: '#ffd54f', type: 'minigolf:butter', duration: 2, eventName: 'BUTTER!' },
        { label: '💰 Money Shot!', color: '#00c853', type: 'minigolf:money-shot', duration: 2, eventName: 'MONEY SHOT!' },
        { label: '🔥 On Fire!', color: '#ff6d00', type: 'minigolf:on-fire', duration: 3, eventName: 'ON FIRE!' },
        { label: '🎩 Pro Move!', color: '#7c4dff', type: 'minigolf:pro-move', duration: 2, eventName: 'PRO MOVE!' },
        { label: '🧊 Ice Cold!', color: '#00b8d4', type: 'minigolf:ice-cold', duration: 2, eventName: 'ICE COLD!' },
        { label: '⚡ Clean!', color: '#76ff03', type: 'minigolf:clean', duration: 2, eventName: 'CLEAN!' },
        { label: '💎 Clutch!', color: '#18ffff', type: 'minigolf:clutch', duration: 3, eventName: 'CLUTCH!' },
        { label: '🏌️ Textbook!', color: '#69f0ae', type: 'minigolf:textbook', duration: 2, eventName: 'TEXTBOOK!' },
      ]
    },
    {
      title: '⛳ Great Shots',
      events: [
        { label: '🐦 Birdie!', color: '#ffd700', type: 'minigolf:birdie', duration: 3, eventName: 'BIRDIE!' },
        { label: '🦅 Eagle!', color: '#ffab00', type: 'minigolf:eagle', duration: 4, eventName: 'EAGLE!' },
        { label: '⭐ HOLE IN ONE!', color: '#ffd700', type: 'minigolf:hole-in-one', duration: 5, eventName: 'HOLE IN ONE!' },
        { label: '🏆 Perfect Round!', color: '#ffd700', type: 'minigolf:perfect-round', duration: 5, eventName: 'PERFECT ROUND!' },
        { label: '🎰 From Downtown!', color: '#d500f9', type: 'minigolf:downtown', duration: 3, eventName: 'FROM DOWNTOWN!' },
        { label: '🏦 Bank Shot!', color: '#651fff', type: 'minigolf:bank-shot', duration: 3, eventName: 'BANK SHOT!' },
        { label: '🎱 Off The Wall!', color: '#304ffe', type: 'minigolf:off-the-wall', duration: 3, eventName: 'OFF THE WALL!' },
        { label: '🐌 Slow Roller!', color: '#00bfa5', type: 'minigolf:slow-roller', duration: 3, eventName: 'SLOW ROLLER IN!' },
        { label: '🚀 Full Send!', color: '#ff3d00', type: 'minigolf:full-send', duration: 3, eventName: 'FULL SEND!' },
        { label: '🪃 Ricochet!', color: '#aa00ff', type: 'minigolf:ricochet', duration: 3, eventName: 'RICOCHET!' },
      ]
    },
    {
      title: '⛳ Bad Shots & Misses',
      events: [
        { label: '❌ Miss!', color: '#f44336', type: 'minigolf:bad', duration: 2, eventName: 'MISS!' },
        { label: '💨 Whiff!', color: '#d32f2f', type: 'minigolf:whiff', duration: 2, eventName: 'WHIFF!' },
        { label: '🏌️‍♂️ Shanked!', color: '#b71c1c', type: 'minigolf:shanked', duration: 2, eventName: 'SHANKED!' },
        { label: '🌿 In The Rough!', color: '#558b2f', type: 'minigolf:in-the-rough', duration: 2, eventName: 'IN THE ROUGH!' },
        { label: '💀 Disaster!', color: '#880e4f', type: 'minigolf:disaster', duration: 3, eventName: 'DISASTER!' },
        { label: '😬 Oof!', color: '#e65100', type: 'minigolf:oof', duration: 2, eventName: 'OOF!' },
        { label: '👀 So Close!', color: '#ff6f00', type: 'minigolf:so-close', duration: 2, eventName: 'SO CLOSE!' },
        { label: '💔 Lip Out!', color: '#c62828', type: 'minigolf:lip-out', duration: 3, eventName: 'LIP OUT!' },
        { label: '🪨 Off The Rocks!', color: '#795548', type: 'minigolf:off-the-rocks', duration: 2, eventName: 'OFF THE ROCKS!' },
        { label: '🌊 Water Hazard!', color: '#0277bd', type: 'minigolf:water-hazard', duration: 3, eventName: 'WATER HAZARD!' },
        { label: '🏖️ In The Sand!', color: '#f9a825', type: 'minigolf:in-the-sand', duration: 2, eventName: 'BUNKER!' },
        { label: '🔄 Mulligan!', color: '#9e9e9e', type: 'minigolf:mulligan', duration: 2, eventName: 'MULLIGAN!' },
      ]
    },
    {
      title: '⛳ Obstacles & Hazards',
      events: [
        { label: '🌀 Windmill!', color: '#2196f3', type: 'minigolf:windmill', duration: 3, eventName: 'WINDMILL ACTIVATED!' },
        { label: '🏃 Treadmill!', color: '#ff5722', type: 'minigolf:treadmill', duration: 3, eventName: 'TREADMILL ON!' },
        { label: '💨 Air Jets!', color: '#00bcd4', type: 'minigolf:blowers', duration: 3, eventName: 'AIR JETS FIRING!' },
        { label: '🌀 Through Windmill!', color: '#1565c0', type: 'minigolf:through-windmill', duration: 3, eventName: 'THROUGH THE WINDMILL!' },
        { label: '🏃 Beat Treadmill!', color: '#e64a19', type: 'minigolf:beat-treadmill', duration: 3, eventName: 'BEAT THE TREADMILL!' },
        { label: '💨 Dodged Jets!', color: '#00838f', type: 'minigolf:dodged-jets', duration: 3, eventName: 'DODGED THE JETS!' },
        { label: '🌀 Ate By Windmill!', color: '#283593', type: 'minigolf:ate-by-windmill', duration: 3, eventName: 'EATEN BY THE WINDMILL!' },
        { label: '🏃 Pushed Back!', color: '#bf360c', type: 'minigolf:pushed-back', duration: 3, eventName: 'PUSHED BACK!' },
        { label: '💨 Blown Away!', color: '#006064', type: 'minigolf:blown-away', duration: 3, eventName: 'BLOWN AWAY!' },
        { label: '🎰 Pinball!', color: '#e040fb', type: 'minigolf:pinball', duration: 3, eventName: 'PINBALL MODE!' },
        { label: '🔥 All Obstacles!', color: '#ff1744', type: 'minigolf:all-obstacles', duration: 4, eventName: 'EVERYTHING ON!' },
        { label: '⚡ Chaos Mode!', color: '#d50000', type: 'minigolf:chaos-mode', duration: 4, eventName: 'CHAOS MODE!' },
      ]
    },
    {
      title: '⛳ Crowd & Hype',
      events: [
        { label: '👏 Crowd Goes Wild!', color: '#4caf50', type: 'minigolf:crowd-wild', duration: 3, eventName: 'CROWD GOES WILD!' },
        { label: '😱 Crowd Gasps!', color: '#9c27b0', type: 'minigolf:crowd-gasp', duration: 2, eventName: 'CROWD GASPS!' },
        { label: '🤣 Crowd Laughs!', color: '#ff9800', type: 'minigolf:crowd-laugh', duration: 2, eventName: 'HAHAHA!' },
        { label: '😤 Pressure!', color: '#f44336', type: 'minigolf:pressure', duration: 3, eventName: 'THE PRESSURE!' },
        { label: '🍿 Popcorn Moment!', color: '#ffeb3b', type: 'minigolf:popcorn', duration: 2, eventName: 'POPCORN TIME!' },
        { label: '🥶 Cold Blooded!', color: '#00e5ff', type: 'minigolf:cold-blooded', duration: 3, eventName: 'COLD BLOODED!' },
        { label: '🔔 Final Putt!', color: '#ff6d00', type: 'minigolf:final-putt', duration: 3, eventName: 'FINAL PUTT!' },
        { label: '⏰ Sudden Death!', color: '#d50000', type: 'minigolf:sudden-death', duration: 4, eventName: 'SUDDEN DEATH!' },
        { label: '🏁 Victory Lap!', color: '#ffd700', type: 'minigolf:victory-lap', duration: 4, eventName: 'VICTORY LAP!' },
        { label: '📈 Bull Market Putt!', color: '#00c853', type: 'minigolf:bull-putt', duration: 3, eventName: 'BULL MARKET PUTT!' },
        { label: '📉 Bear Trap!', color: '#d32f2f', type: 'minigolf:bear-trap', duration: 3, eventName: 'BEAR TRAP!' },
        { label: '🐱 Dead Cat!', color: '#ff8c00', type: 'minigolf:dead-cat', duration: 3, eventName: 'DEAD CAT!' },
      ]
    },
    {
      title: 'Party Vibes',
      events: [
        { label: 'Rainbow', color: '#ff6b6b', type: 'party:rainbow', duration: 6, eventName: 'RAINBOW' },
        { label: 'Disco Ball', color: '#9c27b0', type: 'party:disco', duration: 6, eventName: 'DISCO' },
        { label: 'Fire', color: '#d50000', type: 'party:fire', duration: 5, eventName: 'FIRE' },
        { label: 'Water', color: '#0064c8', type: 'party:water', duration: 5, eventName: 'OCEAN VIBES' },
        { label: 'Canada', color: '#f44336', type: 'party:canada', duration: 5, eventName: 'O CANADA' },
        { label: 'Pot of Gold', color: '#ffd700', type: 'party:gold', duration: 5, eventName: 'POT OF GOLD' },
        { label: 'Confetti', color: '#ff9800', type: 'party:confetti', duration: 5, eventName: 'PARTY!' },
        { label: 'Northern Lights', color: '#00e676', type: 'party:northern-lights', duration: 6, eventName: 'AURORA BOREALIS' }
      ]
    },
    {
      title: 'Award Ceremony (Placements)',
      events: [
        { label: '🥇 1st Place', color: '#ffd700', type: 'ceremony:top3', duration: 8, eventName: '1ST PLACE!' },
        { label: '🥈 2nd Place', color: '#c0c0c0', type: 'ceremony:top3', duration: 8, eventName: '2ND PLACE!' },
        { label: '🥉 3rd Place', color: '#cd7f32', type: 'ceremony:top3', duration: 8, eventName: '3RD PLACE!' },
        { label: '4th Place', color: '#7c4dff', type: 'ceremony:reveal', duration: 6, eventName: '4TH PLACE!' },
        { label: '5th Place', color: '#536dfe', type: 'ceremony:reveal', duration: 6, eventName: '5TH PLACE!' },
        { label: '6th Place', color: '#448aff', type: 'ceremony:reveal', duration: 6, eventName: '6TH PLACE!' },
        { label: '7th Place', color: '#40c4ff', type: 'ceremony:reveal', duration: 6, eventName: '7TH PLACE!' },
        { label: '8th Place', color: '#18ffff', type: 'ceremony:reveal', duration: 6, eventName: '8TH PLACE!' },
        { label: '9th Place', color: '#64ffda', type: 'ceremony:reveal', duration: 6, eventName: '9TH PLACE!' },
        { label: '10th Place', color: '#69f0ae', type: 'ceremony:reveal', duration: 6, eventName: '10TH PLACE!' },
        { label: '💀 Last Place', color: '#d50000', type: 'ceremony:last', duration: 8, eventName: 'LAST PLACE!' }
      ]
    },
    {
      title: 'Sprite Animations (Pixel Art)',
      events: [
        { label: 'Bull Run', color: '#4caf50', type: 'sprite:bull', duration: 10, eventName: 'BULL RUN' },
        { label: 'Bear Attack', color: '#f44336', type: 'sprite:bear', duration: 10, eventName: 'BEAR ATTACK' },
        { label: 'Dancing', color: '#e6c200', type: 'sprite:dancer', duration: 10, eventName: 'DANCE PARTY' },
        { label: 'Briefcase Run', color: '#ffd700', type: 'sprite:briefcase-run', duration: 10, eventName: 'MONEY RUN' },
        { label: 'Dead Cat Bounce', color: '#ff8c00', type: 'sprite:dead-cat', duration: 10, eventName: 'BOUNCE' },
        { label: 'Sinking Ship', color: '#0064c8', type: 'sprite:sinking-ship', duration: 12, eventName: 'SINKING' },
        { label: 'Rocket Launch', color: '#ff5722', type: 'sprite:rocket', duration: 8, eventName: 'LIFTOFF' },
        { label: 'Money Printer', color: '#2e7d32', type: 'sprite:money-printer', duration: 10, eventName: 'BRRR' },
        { label: 'Champagne', color: '#ffc107', type: 'sprite:champagne', duration: 8, eventName: 'CHEERS' },
        { label: 'Coin Waterfall', color: '#ffab00', type: 'sprite:coins', duration: 10, eventName: 'JACKPOT' },
        { label: 'Lightning', color: '#fff176', type: 'sprite:lightning', duration: 8, eventName: 'ZAP' },
        { label: 'Conga Line', color: '#ab47bc', type: 'sprite:conga', duration: 12, eventName: 'CONGA' },
        { label: 'KOMOK Logo', color: '#BA55D3', type: 'sprite:komok', duration: 10, eventName: 'KOMOK' },
        { label: 'KOOK Logo', color: '#5143ED', type: 'sprite:kook', duration: 10, eventName: 'KOOK' }
      ]
    }
  ];

  return (
    <div style={{ padding: '20px', color: '#fff' }}>
      <h2 style={{ marginBottom: '10px' }}>📡 Event Tester</h2>

      {/* Current State Display */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        padding: '15px',
        background: '#1a1a2e',
        borderRadius: '8px'
      }}>
        <div>
          <span style={{ color: '#888', fontSize: '12px' }}>Current Phase:</span>
          <div style={{ fontWeight: 'bold', color: '#a5b4fc' }}>{currentPhase}</div>
        </div>
        <div>
          <span style={{ color: '#888', fontSize: '12px' }}>Trading Block:</span>
          <div style={{ fontWeight: 'bold', color: '#a5b4fc' }}>{tradingBlock || 'N/A'}</div>
        </div>
        <div>
          <span style={{ color: '#888', fontSize: '12px' }}>Market:</span>
          <div style={{ fontWeight: 'bold', color: marketOpen ? '#22c55e' : '#ef4444' }}>
            {marketOpen ? 'OPEN' : 'CLOSED'}
          </div>
        </div>
        <div>
          <span style={{ color: '#888', fontSize: '12px' }}>ESP32 Panels:</span>
          <div style={{ fontWeight: 'bold', color: esp32Panels.length > 0 ? '#22c55e' : '#ef4444' }}>
            {esp32Panels.length} connected
          </div>
          {esp32Panels.length > 0 && (
            <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
              {esp32Panels.map(p => p.type || p.id.slice(0, 6)).join(', ')}
            </div>
          )}
        </div>
      </div>

      {message.text && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '20px',
          borderRadius: '6px',
          background: message.type === 'success' ? '#1b5e20' : '#b71c1c',
          color: message.type === 'success' ? '#a5d6a7' : '#ef9a9a'
        }}>
          {message.text}
        </div>
      )}

      {/* Jump to Phase Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        border: '2px solid #f59e0b'
      }}>
        <h3 style={{ marginBottom: '5px', fontSize: '18px', color: '#f59e0b' }}>
          🎯 Jump to Any Phase (Testing)
        </h3>
        <p style={{ color: '#888', fontSize: '12px', marginBottom: '15px' }}>
          Skip to any phase for testing. These buttons set the party state directly.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '10px'
        }}>
          {jumpToPhaseButtons.map((btn, i) => (
            <button
              key={i}
              onClick={btn.action}
              disabled={loading}
              title={btn.desc}
              style={{
                padding: '12px',
                border: 'none',
                borderRadius: '8px',
                background: btn.color,
                color: ['#ffd700', '#f59e0b'].includes(btn.color) ? '#000' : '#fff',
                fontWeight: 'bold',
                fontSize: '13px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Party Flow Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        border: '2px solid #22c55e'
      }}>
        <h3 style={{ marginBottom: '5px', fontSize: '18px', color: '#22c55e' }}>
          🎬 Full Party Flow (Sequential)
        </h3>
        <p style={{ color: '#888', fontSize: '12px', marginBottom: '15px' }}>
          Follow the timeline in order. <span style={{ color: '#ef4444' }}>HARD</span> = auto-triggers,
          <span style={{ color: '#22c55e' }}> soft</span> = visual guide only.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '8px'
        }}>
          {partyFlowSteps.map((step, i) => (
            <button
              key={i}
              onClick={() => sendEvent(step.endpoint, step.body)}
              disabled={loading}
              style={{
                padding: '10px 12px',
                border: step.isHard ? '2px solid #ef4444' : 'none',
                borderRadius: '6px',
                background: step.color,
                color: ['#ffd700', '#c0c0c0', '#cd7f32', '#00bcd4', '#ffeb3b'].includes(step.color) ? '#000' : '#fff',
                fontWeight: 'bold',
                fontSize: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '10px',
                flexShrink: 0
              }}>
                {step.step}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {step.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Power Hour Toggle */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b0e 100%)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        border: '2px solid #ff5722'
      }}>
        <h3 style={{ marginBottom: '5px', fontSize: '18px', color: '#ff5722' }}>
          Power Hour (LED Ambient Test)
        </h3>
        <p style={{ color: '#888', fontSize: '12px', marginBottom: '15px' }}>
          Visual only — does NOT change game state. Pi gets pulsing red/orange background + sparks, M4 header goes orange, both scroll 2x speed.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={startPowerHour}
            disabled={loading}
            style={{
              padding: '12px 24px', border: 'none', borderRadius: '8px',
              background: '#ff5722', color: '#fff', fontWeight: 'bold', fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1
            }}
          >
            Start Power Hour
          </button>
          <button
            onClick={stopPowerHour}
            disabled={loading}
            style={{
              padding: '12px 24px', border: 'none', borderRadius: '8px',
              background: '#455a64', color: '#fff', fontWeight: 'bold', fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1
            }}
          >
            Stop Power Hour
          </button>
        </div>
      </div>

      {/* Visual Effects Section */}
      {effectGroups.map((group, gi) => (
        <div key={gi} style={{
          background: '#1a1a2e',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <h3 style={{ marginBottom: '10px', fontSize: '16px' }}>{group.title}</h3>
          {group.title === 'Party Vibes' && (
            <div style={{ marginBottom: '10px' }}>
              <input
                type="text"
                placeholder="Custom overlay text (optional)"
                value={customPartyText}
                onChange={(e) => setCustomPartyText(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  background: '#0d0d1a',
                  color: '#fff',
                  fontSize: '13px',
                  width: '280px'
                }}
              />
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {group.events.map((event, ei) => (
              <button
                key={ei}
                onClick={() => event.isCountdown
                  ? sendEvent(event.endpoint, event.body, `Countdown started`)
                  : sendEvent('/api/admin/events/broadcast', {
                    type: event.type,
                    duration: event.duration,
                    tickers: event.tickers,
                    changePercent: event.changePercent,
                    eventName: event.type.startsWith('party:') ? (customPartyText || '') : event.eventName,
                    ...event.data
                  })
                }
                disabled={loading}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  background: event.color,
                  color: ['#ffeb3b', '#ffd700', '#ffc107', '#cddc39'].includes(event.color) ? '#000' : '#fff',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {event.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Quick Reference */}
      <div style={{
        background: '#1a1a2e',
        borderRadius: '8px',
        padding: '15px',
        marginTop: '20px'
      }}>
        <h3 style={{ marginBottom: '10px', fontSize: '16px' }}>📋 Quick Reference</h3>
        <div style={{ color: '#888', fontSize: '13px', lineHeight: '1.8' }}>
          <p><strong style={{ color: '#ef4444' }}>HARD Countdowns</strong> (auto-trigger):</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '10px' }}>
            <li>Opening Bell → auto-opens market + starts Phase 1</li>
            <li>Macro Game → auto-pauses trading</li>
            <li>Closing Bell → auto-closes market</li>
          </ul>
          <p><strong style={{ color: '#22c55e' }}>SOFT Countdowns</strong> (visual guide):</p>
          <ul style={{ paddingLeft: '20px' }}>
            <li>Trading sessions (30 min for 1-3, 5 min for Power Hour)</li>
            <li>Admin manually triggers next phase when ready</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminEventTester;
